import { useRoutes, type RouteObject } from "react-router";
import { Layout } from "./layout/Layout";
import { Home } from "./pages/Home";
import { Platform } from "./pages/Platform";
import { Features } from "./pages/Features";
import { Pricing } from "./pages/Pricing";
import { Customers } from "./pages/Customers";
import { About } from "./pages/About";
import { Contact } from "./pages/Contact";
import { Demo } from "./pages/Demo";
import { SmsOptIn } from "./pages/SmsOptIn";
import { Careers } from "./pages/Careers";
import { Press } from "./pages/Press";
import { Sitemap } from "./pages/Sitemap";
import { NotFound } from "./pages/NotFound";
import { Resources } from "./pages/Resources";
import { MedicationRegimenReviewSoftware } from "./pages/MedicationRegimenReviewSoftware";
import { ConsultantPharmacistSoftware } from "./pages/ConsultantPharmacistSoftware";
import { SurveyReadiness } from "./pages/SurveyReadiness";
import { RxpertiseAlternative } from "./pages/compare/RxpertiseAlternative";
import { FrameworkrxpAlternative } from "./pages/compare/FrameworkrxpAlternative";
import { GerimedAlternative } from "./pages/compare/GerimedAlternative";
import { Profiles360Alternative } from "./pages/compare/Profiles360Alternative";
import { CompareHub } from "./pages/compare/CompareHub";
import { RoiCalculator } from "./pages/RoiCalculator";
import { FtagChecklist } from "./pages/resources/FtagChecklist";
import { GdrTemplate } from "./pages/resources/GdrTemplate";
import { BlogGateway } from "./pages/BlogGateway";
import { Docs } from "./pages/Docs";
import { SnfSolution } from "./pages/solutions/Snf";
import { AscSolution } from "./pages/solutions/Asc";
import { DialysisSolution } from "./pages/solutions/Dialysis";
import { Trust } from "./pages/trust/Trust";
import { Security } from "./pages/trust/Security";
import { Hipaa } from "./pages/trust/Hipaa";
import { Privacy } from "./pages/trust/Privacy";
import { Terms } from "./pages/trust/Terms";
import { Cookies } from "./pages/trust/Cookies";
import { Dpa } from "./pages/trust/Dpa";
import { Baa } from "./pages/trust/Baa";
import { Sla } from "./pages/trust/Sla";
import { Aup } from "./pages/trust/Aup";
import { Subprocessors } from "./pages/trust/Subprocessors";
import { Accessibility } from "./pages/trust/Accessibility";
import { Gdpr } from "./pages/trust/Gdpr";
import { Ccpa } from "./pages/trust/Ccpa";
import { Status } from "./pages/trust/Status";
import { ResponsibleDisclosure } from "./pages/trust/ResponsibleDisclosure";
import { SmsTerms } from "./pages/trust/SmsTerms";
import { AspMethodologyPage } from "./pages/trust/AspMethodologyPage";
import { DdiMethodologyPage } from "./pages/trust/DdiMethodologyPage";
import { MedCartInspectionMethodologyPage } from "./pages/trust/MedCartInspectionMethodologyPage";
import { NarcoticDestructionMethodologyPage } from "./pages/trust/NarcoticDestructionMethodologyPage";
import { PlatformAiBubbleMethodology } from "./pages/trust/PlatformAiBubbleMethodology";
import { ClinicalToolkitMethodology } from "./pages/trust/ClinicalToolkitMethodology";
import { RegulatoryHorizonMethodology } from "./pages/trust/RegulatoryHorizonMethodology";
import { LicensingMethodology } from "./pages/trust/LicensingMethodology";
import { HelpAiMethodology } from "./pages/trust/HelpAiMethodology";
import { RegulatoryAiMethodology } from "./pages/trust/RegulatoryAiMethodology";

// Route table. Static (non-param) routes are prerendered to their own HTML at
// build by scripts/prerender.mjs; /blog* is excluded from prerender (served by
// the edge Worker) and "*" is the SPA 404. Layout renders <Outlet/> for the
// children.
//
// PRERENDER CONTRACT: the script walks this table and prerenders every route
// whose full path contains no ":" and is not "*". Adding a plain static route
// here is enough to get it prerendered — nothing else to register.
export const routes: RouteObject[] = [
  {
    element: <Layout />,
    children: [
      { index: true, element: <Home /> },
      { path: "platform", element: <Platform /> },
      { path: "features", element: <Features /> },
      { path: "pricing", element: <Pricing /> },
      // SEO cluster landing pages (paid-search final URLs + organic targets).
      { path: "medication-regimen-review-software", element: <MedicationRegimenReviewSoftware /> },
      { path: "consultant-pharmacist-software", element: <ConsultantPharmacistSoftware /> },
      { path: "survey-readiness", element: <SurveyReadiness /> },
      { path: "compare/rxpertise-alternative", element: <RxpertiseAlternative /> },
      { path: "compare/frameworkrxp-alternative", element: <FrameworkrxpAlternative /> },
      { path: "compare/gerimed-alternative", element: <GerimedAlternative /> },
      { path: "compare/profiles-360-alternative", element: <Profiles360Alternative /> },
      { path: "compare", element: <CompareHub /> },
      { path: "roi-calculator", element: <RoiCalculator /> },
      { path: "resources/f-tag-readiness-checklist", element: <FtagChecklist /> },
      { path: "resources/gdr-documentation-template", element: <GdrTemplate /> },
      { path: "customers", element: <Customers /> },
      { path: "about", element: <About /> },
      { path: "contact", element: <Contact /> },
      { path: "demo", element: <Demo /> },
      { path: "sms-opt-in", element: <SmsOptIn /> },
      { path: "careers", element: <Careers /> },
      { path: "press", element: <Press /> },
      { path: "sitemap", element: <Sitemap /> },
      { path: "resources", element: <Resources /> },
      // The blog is server-rendered by the API and served at /blog* by an edge
      // Cloudflare Worker. These routes only fire on in-app <Link> navigation;
      // the gateway forces a full load so the Worker serves it. Not prerendered.
      { path: "blog", element: <BlogGateway /> },
      { path: "blog/:slug", element: <BlogGateway /> },
      { path: "docs", element: <Docs /> },
      { path: "solutions/snf", element: <SnfSolution /> },
      { path: "solutions/asc", element: <AscSolution /> },
      { path: "solutions/dialysis", element: <DialysisSolution /> },
      { path: "trust", element: <Trust /> },
      { path: "security", element: <Security /> },
      { path: "hipaa", element: <Hipaa /> },
      { path: "privacy", element: <Privacy /> },
      { path: "terms", element: <Terms /> },
      { path: "cookies", element: <Cookies /> },
      { path: "dpa", element: <Dpa /> },
      { path: "baa", element: <Baa /> },
      { path: "sla", element: <Sla /> },
      { path: "aup", element: <Aup /> },
      { path: "subprocessors", element: <Subprocessors /> },
      { path: "accessibility", element: <Accessibility /> },
      { path: "gdpr", element: <Gdpr /> },
      { path: "ccpa", element: <Ccpa /> },
      { path: "status", element: <Status /> },
      { path: "responsible-disclosure", element: <ResponsibleDisclosure /> },
      { path: "sms-terms", element: <SmsTerms /> },
      { path: "trust/asp-methodology", element: <AspMethodologyPage /> },
      { path: "trust/ddi-methodology", element: <DdiMethodologyPage /> },
      { path: "trust/med-cart-inspection-methodology", element: <MedCartInspectionMethodologyPage /> },
      { path: "trust/narcotic-destruction-compliance", element: <NarcoticDestructionMethodologyPage /> },
      { path: "trust/platform-ai-bubble-methodology", element: <PlatformAiBubbleMethodology /> },
      { path: "trust/clinical-toolkit-methodology", element: <ClinicalToolkitMethodology /> },
      { path: "trust/regulatory-horizon-methodology", element: <RegulatoryHorizonMethodology /> },
      { path: "trust/licensing-methodology", element: <LicensingMethodology /> },
      { path: "trust/help-ai-methodology", element: <HelpAiMethodology /> },
      { path: "trust/regulatory-ai-methodology", element: <RegulatoryAiMethodology /> },
      // Prerendered as dist/404.html — Render serves it (with a 404 status) for
      // unknown paths, since routes are empty and _redirects only maps known
      // routes to their .html (no catch-all rewrite). "*" keeps client-side SPA
      // 404s working during in-app navigation.
      { path: "404", element: <NotFound /> },
      { path: "*", element: <NotFound /> },
    ],
  },
];

/**
 * Renders the route table. Deliberately shared by BOTH entrypoints — the client
 * (src/main.tsx, inside <BrowserRouter>) and the prerender
 * (scripts/prerender.mjs, inside <StaticRouter>) — so the static HTML and the
 * hydrated tree are produced by the same code. Using useRoutes rather than a
 * data router keeps both sides on the plain declarative API.
 */
export function AppRoutes() {
  return useRoutes(routes);
}
